<script setup lang="ts">
import "@vuepic/vue-datepicker/dist/main.css";
import { DateTime } from "luxon";
import { ref, watch, watchEffect } from "vue";
import { useRoute } from "vue-router";
import Alipay from "../assets/images/payments/alipay.svg";
import Amex from "../assets/images/payments/amex.svg";
import Discover from "../assets/images/payments/discover.svg";
import EftposAustralia from "../assets/images/payments/eftpos.png";
import JCB from "../assets/images/payments/jcb.svg";
import Mastercard from "../assets/images/payments/mastercard.svg";
import UnionPay from "../assets/images/payments/unionpay.svg";
import Visa from "../assets/images/payments/visa.svg";
import WechatPay from "../assets/images/payments/wechatpay.svg";
import { useLoading } from "../composables/useLoading";
import { useNotification } from "../composables/useNotification";
import { FirestoreService } from "../services/firestore";
import { Payment } from "../types/payment";
import { decrypt } from "../utils/encrypt";
import { getStoreName } from "../utils/user-mapping";

const { setText, toggleLoading } = useLoading();
const { setError } = useNotification();
// const { currentStore } = useStores();
const payments = ref<Payment[]>([]);
const paymentsWithOrderId = ref<Payment[]>([]);
watchEffect(async () => {
  // setText("loading payments...");
  // toggleLoading(true);
  try {
    const result = [];
    for (const payment of payments.value) {
      if (payment.merchantReference) {
        const merchantReference = await decrypt(
          payment.merchantReference,
          "your-password-here",
        );
        result.push({
          ...payment,
          merchantReference,
        });
      } else {
        result.push(payment);
      }
    }
    paymentsWithOrderId.value = result.sort((p1, p2) => {
      return (
        DateTime.fromISO(p2.date).toMillis() -
        DateTime.fromISO(p1.date).toMillis()
      );
    });
  } catch (e) {
    setError("failed to load payments please try again");
  } finally {
    // toggleLoading(false);
  }
});
const init = async (
  store: string,
  user: string,
  merchant: string,
  date: string,
) => {
  setText("Loading payments...");
  toggleLoading(true);
  try {
    const path = `/users/${user}/merchants/${merchant}/stores/${store}/days/${date}/payments`;
    console.log({ path });
    payments.value = await FirestoreService.getCollection<Payment>(path);
  } catch (e) {
    setError("failed to load payments please try again");
  } finally {
    toggleLoading(false);
  }
};
const route = useRoute();
const currentDate = ref(route.params.date as string);
const currentStore = ref(route.params.store as string);
const currentUser = ref(route.params.user as string);
const currentMerchant = ref(route.params.merchant as string);
watch(
  [currentDate, currentStore, currentUser, currentMerchant],
  async () => {
    if (!currentDate.value) return;
    if (!currentStore.value) return;
    if (!currentUser.value) return;
    if (!currentMerchant.value) return;
    await init(
      currentStore.value,
      currentUser.value,
      currentMerchant.value,
      currentDate.value,
    );
  },
  { immediate: true },
);
const paymentMethodToImage = (paymentMethod: string) => {
  switch (paymentMethod.toLowerCase()) {
    case "visa":
      return Visa;
    case "alipay":
      return Alipay;
    case "mc":
      return Mastercard;
    case "jcb":
      return JCB;
    case "amex":
      return Amex;
    case "discover":
      return Discover;
    case "cup":
      return UnionPay;
    case "wechatpay":
      return WechatPay;
    case "eftpos_australia":
      return EftposAustralia;
    default:
      return;
  }
};
</script>
<template>
  <div class="px-4 pb-4 sm:pb-6 sm:px-6 lg:px-8 lg:pb-8 pt-10">
    <router-link
      :to="{
        name: 'payments-summary',
      }"
    >
      <a href="#" class="text-primary hover:text-primary underline text-sm"
        >Back to Dashboard</a
      >
    </router-link>
    <h2 class="mb-2 mt-4">{{ getStoreName(currentStore) }}</h2>
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <div class="sm:flex sm:items-center mb-6">
          <div
            class="sm:flex-auto flex flex-row justify-start space-x-2 items-center"
          >
            <a
              href="#"
              class="text-primary underline font-medium text-base font-poppins"
              @click.prevent="
                currentStore &&
                  init(currentStore, currentUser, currentMerchant, currentDate)
              "
              >Reload Payments</a
            >
          </div>
        </div>
        <div class="w-[200px] max-w-full my-4">
          <input v-model="currentDate" type="date" class="border rounded p-2" />
        </div>
      </div>
    </div>
    <div class="p-4 bg-white rounded-[16px]">
      <div class="-mx-4 mt-8 sm:-mx-0">
        <table class="min-w-full" v-if="currentStore">
          <thead class="border-none">
            <tr class="border-none">
              <th
                scope="col"
                class="rounded-l-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Date
              </th>

              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Amount
              </th>

              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Order ID
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Card Type
              </th>
              <th
                scope="col"
                class="rounded-r-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="payment in paymentsWithOrderId" :key="payment.id">
              <td
                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0"
              >
                {{
                  DateTime.fromISO(payment.date)
                    .setZone("Australia/Melbourne")
                    .toFormat("yyyy-MM-dd HH:mm:ss")
                }}
                <dl class="font-normal lg:hidden">
                  <dt class="sr-only">Amount</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    {{
                      (payment.amount.amount / 100).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }}
                  </dd>
                  <dt class="sr-only">Order Id</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    {{ payment.merchantReference }}
                  </dd>
                </dl>
              </td>

              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  (Payment.total(payment) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>

              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{ payment.merchantReference }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                <div class="flex flex-row justify items-center">
                  <img
                    :src="paymentMethodToImage(payment.paymentMethod)"
                    v-if="paymentMethodToImage(payment.paymentMethod)"
                    class="block wx-auto w-[50px] h-auto"
                  />

                  <p v-else>{{ payment.paymentMethod.toUpperCase() }}</p>
                </div>
              </td>
              <td class="px-3 py-4 text-sm text-gray-500">
                {{ "Successful" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
